/**
 * Cliente Hospital Service
 *
 * Handles all hospital-linking operations for a Client:
 * list linked hospitals, link/unlink, bulk operations,
 * representante-hospital associations.
 *
 * Epic 26 - Task 1.5
 */
import apiClient from '../apiClient';
import type { ClienteHospital } from '../../types/clienteHospital';

class ClienteHospitalService {
  private readonly baseUrl = '/clientes';

  /**
   * Lista hospitais atendidos pelo cliente (vinculados)
   * Retorna dados completos do hospital + informações de vínculo
   */
  async getClienteHospitais(clienteId: number): Promise<ClienteHospital[]> {
    const response = await apiClient.get<ClienteHospital[]>(
      `${this.baseUrl}/${clienteId}/hospitais?ativo=true`
    );
    return response.data;
  }

  /**
   * Vincula um hospital ao cliente com código específico
   */
  async vincularHospitalSingle(
    clienteId: number,
    hospitalId: number,
    codigo: string,
    dataInicio: string
  ): Promise<ClienteHospital> {
    const response = await apiClient.post<ClienteHospital>(
      `${this.baseUrl}/${clienteId}/hospitais`,
      {
        hospital_id: hospitalId,
        codigo,
        data_inicio: dataInicio,
        ativo: true
      }
    );
    return response.data;
  }

  /**
   * Vincula um ou mais hospitais ao cliente (bulk)
   */
  async vincularHospitais(
    clienteId: number,
    hospitalIds: number[]
  ): Promise<{ success: boolean; message: string; vinculos_criados: number }> {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      vinculos_criados: number;
    }>(`${this.baseUrl}/${clienteId}/hospitais/bulk`, { hospital_ids: hospitalIds });
    return response.data;
  }

  /**
   * Desvincula hospital do cliente
   */
  async desvincularHospital(
    clienteId: number,
    hospitalId: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>(
      `${this.baseUrl}/${clienteId}/hospitais/${hospitalId}`
    );
    return response.data;
  }

  /**
   * Lista todos os hospitais disponíveis no sistema
   * Para uso no modal de vinculação
   */
  async getTodosHospitais(): Promise<any[]> {
    const response = await apiClient.get<any[]>('/hospitais');
    return response.data;
  }

  /**
   * Obtém o próximo código sequencial disponível para o cliente
   */
  async getNextHospitalCode(clienteId: number): Promise<string> {
    const response = await apiClient.get<{ next_code: string }>(
      `${this.baseUrl}/${clienteId}/hospitais/next-code`
    );
    return response.data.next_code;
  }

  /**
   * Lista apenas os hospitais vinculados a um cliente específico
   * Para uso no modal de vinculação de representante-hospital
   */
  async getHospitaisDoCliente(clienteId: number): Promise<any[]> {
    const response = await apiClient.get<any[]>(`${this.baseUrl}/${clienteId}/hospitais?ativo=true`);
    return response.data;
  }

  /**
   * @deprecated Use getClienteHospitais() - mantido para compatibilidade
   */
  async getHospitaisByCliente(clienteId: number): Promise<any[]> {
    const response = await apiClient.get<any[]>(
      `${this.baseUrl}/${clienteId}/hospitais?ativo=true`
    );
    return response.data;
  }

  /**
   * Lista hospitais vinculados a um representante específico
   */
  async getRepresentanteHospitais(
    clienteId: number,
    representanteId: number
  ): Promise<any[]> {
    const response = await apiClient.get<any[]>(
      `${this.baseUrl}/${clienteId}/representantes/${representanteId}/hospitais`
    );
    return response.data;
  }

  /**
   * Vincula hospitais a um representante
   */
  async vincularRepresentanteHospitais(
    clienteId: number,
    representanteId: number,
    hospitalIds: number[]
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      `${this.baseUrl}/${clienteId}/representantes/${representanteId}/hospitais/bulk`,
      { hospital_ids: hospitalIds }
    );
    return response.data;
  }
}

export default new ClienteHospitalService();
